import loginBanner1 from '@/assets/images/login-img-1.png';
import loginBanner2 from '@/assets/images/login-img-2.png';
import loginBanner3 from '@/assets/images/login-img-3.png';
import noise from '@/assets/images/noise.png';
import stars from '@/assets/images/stars.svg';
import arrowLeft from '@/assets/svgs/icons/arrow-left.svg';
import arrowRight from '@/assets/svgs/icons/arrow-right.svg';
import { DotButton, useDotButton } from '@/components/Login/CarouselDots.tsx';
import {
  NextButton,
  PrevButton,
  usePrevNextButtons,
} from '@/components/Login/CarouselNavigation.tsx';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from '@superfunnel/ui-core/v2/Carousel';
import Autoplay from 'embla-carousel-autoplay';
import Fade from 'embla-carousel-fade';
import { useEffect, useRef, useState } from 'react';

const CarouselBox = () => {
  const sliderDelay = 3000;
  const [api, setApi] = useState<CarouselApi>();
  const [isPlaying, setIsPlaying] = useState(true);
  const { selectedIndex, onDotButtonClick } = useDotButton(api);
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(api);

  const autoplay = useRef(
    Autoplay({
      delay: sliderDelay,
      stopOnMouseEnter: false,
      stopOnInteraction: false,
    }),
  );

  const carouselData = [
    {
      id: 1,
      img: loginBanner1,
      title: 'AI-Powered Page Builder',
      text: 'Generate a high-converting landing page in seconds — just connect your ad account and type a prompt.',
    },
    {
      id: 2,
      img: loginBanner2,
      title: 'Smart Personalization',
      text: 'Every visitor sees the most relevant version, thanks to real-time traffic routing powered by AI.',
    },
    {
      id: 3,
      img: loginBanner3,
      title: 'AI-Driven Optimization',
      text: 'SuperFunnel tests, learns, and suggests improvements—so your funnel gets smarter every day.',
    },
  ];

  useEffect(() => {
    if (!api) return;

    const onStop = () => setIsPlaying(false);
    const onPlay = () => setIsPlaying(true);

    api.on('autoplay:stop', onStop);
    api.on('autoplay:play', onPlay);

    return () => {
      api.off('autoplay:stop', onStop);
      api.off('autoplay:play', onPlay);
    };
  }, [api]);

  return (
    <div className={'relative hidden h-screen py-2 pr-2 lg:block'}>
      <div
        className={
          'relative flex h-full w-full flex-col overflow-hidden rounded-[22px] bg-[linear-gradient(180deg,rgba(95,119,254,0.03)_0%,#5F77FE_100%)] pt-13 pb-26'
        }
      >
        <img
          className="absolute top-0 left-0 h-full w-full object-cover opacity-50 mix-blend-overlay"
          src={noise}
          alt=""
        />
        <img
          className="absolute top-0 left-0 h-full w-full object-cover"
          src={stars}
          alt=""
        />
        <Carousel
          plugins={[autoplay.current, Fade()]}
          opts={{ loop: true, duration: 50 }}
          setApi={setApi}
          className="grid h-full w-full grow grid-rows-[1fr_auto]"
          onMouseEnter={() => {
            autoplay.current.stop();
            setIsPlaying(false);
          }}
          onMouseLeave={() => {
            autoplay.current.play();
            setIsPlaying(true);
          }}
        >
          <CarouselContent className={'h-full pb-7'}>
            {carouselData.map((item) => (
              <CarouselItem
                key={item.id}
                className={'flex h-full flex-col items-center justify-center'}
              >
                <div className="relative w-full flex-1">
                  <img
                    className="absolute inset-0 h-full w-full object-contain"
                    src={item.img}
                    alt="Carousel Image"
                  />
                </div>
                <div className={'shrink-0 space-y-2'}>
                  <p
                    className={
                      'max-w-[440px] text-center text-[22px]/[1.4] font-medium text-white'
                    }
                  >
                    {item.title}
                  </p>
                  <p
                    className={
                      'font-inter max-w-[440px] text-center text-[16px]/[1.4] text-white'
                    }
                  >
                    {item.text}
                  </p>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>

          <div className="relative flex w-full items-center justify-center gap-6">
            <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled}>
              <img src={arrowLeft} alt="" />
            </PrevButton>
            <div className={'flex items-center gap-[7px]'}>
              {carouselData.map((item) => (
                <DotButton
                  key={item.id - 1}
                  onClick={() => {
                    onDotButtonClick(item.id - 1);
                    autoplay.current.stop();
                    setIsPlaying(false);
                  }}
                  className={`block size-1.5 cursor-pointer overflow-hidden rounded-full bg-white/40 transition-all ${item.id - 1 === selectedIndex ? 'w-10' : ''}`}
                >
                  {item.id - 1 === selectedIndex && (
                    <span
                      key={selectedIndex}
                      style={{
                        animation: `${sliderDelay}ms linear forwards progress`,
                        animationPlayState: isPlaying ? 'running' : 'paused',
                      }}
                      className="block h-full bg-white"
                    />
                  )}
                </DotButton>
              ))}
            </div>
            <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled}>
              <img src={arrowRight} alt="" />
            </NextButton>
          </div>
        </Carousel>
      </div>
    </div>
  );
};

export default CarouselBox;
